import DescBlock from '@/layouts/game-settings/components/DescBlock'
import Block from '@/layouts/game-settings/components/Block'
import { FieldGroup, ImageUploader } from '@/ui'
import { Controller, useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { FormErrorMessage, Input, Select, Toggle } from '@dashboard/ui'
import {
  BonusDisplayType,
  CarouselDisplayType,
  QuickStoreButtonMode,
  StoreCardVersion,
  StoreImageSize,
  StoreTextSize,
  WebsiteRead,
} from '@/api/dashboard'
import { getRecommendedSizeByRatio } from '@/libs'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '@/api/useCurrentUser'
import { IMAGE_RATIO_OPTIONS } from '@/layouts/hub-settings/types'
import { SuperAdminFieldGroup } from '@/components/ui/FieldGroup/SuperAdminFieldGroup'

export const StoreSettings = () => {
  const { t } = useTranslation()
  const { canEdit, isSuperAdmin } = useCurrentUser()
  const {
    formState: { errors: errorsWebSite },
    register,
    watch,
    control,
  } = useFormContext<WebsiteRead>()
  const [imageRatio] = watch(['config.store_settings.image_ratio'])
  const recommendedSizeByRatio = getRecommendedSizeByRatio(imageRatio || 0)

  return (
    <DescBlock title={t('hub-settings.store-settings.title')} description={t('hub-settings.store-settings.desc')}>
      <Block>
        <FieldGroup label={t('hub-settings.store-settings.image-ratio')}>
          <Controller
            control={control}
            name="config.store_settings.image_ratio"
            render={({ field }) => <Select items={IMAGE_RATIO_OPTIONS} {...field} disabled={!canEdit} />}
          />
        </FieldGroup>

        <FieldGroup label={t('hub-settings.store-settings.image-place-holder')}>
          <Controller
            control={control}
            name="config.store_settings.default_image_url"
            render={({ field }) => (
              <ImageUploader
                disabled={!canEdit}
                accept={{
                  'image/jpeg': ['.jpeg', '.jpg'],
                  'image/png': [],
                  'image/webp': [],
                }}
                recommendedSize={recommendedSizeByRatio}
                {...field}
              />
            )}
          />
          <ErrorMessage
            name="config.store_settings.default_image_url"
            errors={errorsWebSite}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>

        {isSuperAdmin && (
          <SuperAdminFieldGroup label={'Store Item View'}>
            <Controller
              control={control}
              name="config.store_settings.card_version"
              render={({ field }) => (
                <Select
                  items={[
                    { value: StoreCardVersion.V1, children: 'V1', disabled: true },
                    { value: StoreCardVersion.V2, children: 'V2', disabled: true },
                    { value: StoreCardVersion.V3, children: 'V3' },
                    { value: StoreCardVersion.New, children: 'New' },
                  ]}
                  {...field}
                />
              )}
            />
          </SuperAdminFieldGroup>
        )}

        {canEdit && (
          <FieldGroup label={t('hub-settings.store-settings.image-size')}>
            <Controller
              control={control}
              name="config.store_settings.image_size"
              render={({ field }) => (
                <Select
                  items={[
                    { value: StoreImageSize.Cover, children: 'Cover' },
                    { value: StoreImageSize.Contain, children: 'Contain' },
                    { value: StoreImageSize.Fill, children: 'Fill' },
                    { value: StoreImageSize.None, children: 'None' },
                    { value: StoreImageSize.ScaleDown, children: 'Scale Down' },
                  ]}
                  {...field}
                />
              )}
            />
          </FieldGroup>
        )}

        {canEdit && (
          <FieldGroup label={t('hub-settings.store-settings.text-size')}>
            <Controller
              control={control}
              name="config.store_settings.text_size"
              render={({ field }) => (
                <Select
                  items={[
                    { value: StoreTextSize.Auto, children: 'Auto (default)' },
                    { value: StoreTextSize.Small, children: 'Small' },
                    { value: StoreTextSize.Normal, children: 'Normal' },
                    { value: StoreTextSize.Large, children: 'Large' },
                  ]}
                  {...field}
                />
              )}
            />
          </FieldGroup>
        )}

        {canEdit && (
          <FieldGroup label={t('hub-settings.store-settings.bonus-variant')}>
            <Controller
              control={control}
              name="config.store_settings.bonus_display"
              render={({ field }) => (
                <Select
                  items={[
                    {
                      value: BonusDisplayType.Default,
                      children: t('hub-settings.store-settings.bonus-variant.auto'),
                      caption: t('hub-settings.store-settings.bonus-variant.auto.caption'),
                    },
                    {
                      value: BonusDisplayType.Strikethrough,
                      children: t('hub-settings.store-settings.bonus-variant.strikethrough'),
                    },
                    {
                      value: BonusDisplayType.BasePlus,
                      children: t('hub-settings.store-settings.bonus-variant.base-plus'),
                    },
                  ]}
                  {...field}
                />
              )}
            />
          </FieldGroup>
        )}

        {canEdit && (
          <FieldGroup label={t('hub-settings.store-settings.carousel-display')}>
            <Controller
              control={control}
              name="config.store_settings.carousel_display"
              render={({ field }) => (
                <Select
                  items={[
                    {
                      value: CarouselDisplayType.Default,
                      children: t('hub-settings.store-settings.carousel-display.default'),
                    },
                    {
                      value: CarouselDisplayType.Compact,
                      children: t('hub-settings.store-settings.carousel-display.compact'),
                    },
                    {
                      value: CarouselDisplayType.Alone,
                      children: t('hub-settings.store-settings.carousel-display.alone'),
                    },
                  ]}
                  {...field}
                />
              )}
            />
          </FieldGroup>
        )}

        {canEdit && (
          <div className="flex w-full justify-between">
            <div className="mr-4 pb-2">
              <FieldGroup caption={t('hub-settings.store-settings.show_sticky_categories')}> </FieldGroup>
            </div>
            <Controller
              control={control}
              name="config.store_settings.show_sticky_categories"
              render={({ field }) => (
                <Toggle
                  size="sm"
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  disabled={!canEdit}
                />
              )}
            />
          </div>
        )}

        {isSuperAdmin && (
          <div className="flex w-full justify-between">
            <div className="mr-4 pb-2">
              <SuperAdminFieldGroup caption={t('hub-settings.store-settings.show-description')}> </SuperAdminFieldGroup>
            </div>
            <Controller
              control={control}
              name="config.store_settings.show_item_description"
              render={({ field }) => (
                <Toggle
                  size="sm"
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  disabled={!canEdit}
                />
              )}
            />
          </div>
        )}

        {isSuperAdmin && (
          <div className="flex w-full justify-between">
            <div className="mr-4 pb-2">
              <SuperAdminFieldGroup caption={t('hub-settings.store-settings.show-reward-points')}>
                {' '}
              </SuperAdminFieldGroup>
            </div>
            <Controller
              control={control}
              name="config.store_settings.show_reward_points_label"
              render={({ field }) => (
                <Toggle
                  size="sm"
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  disabled={!canEdit}
                />
              )}
            />
          </div>
        )}

        {isSuperAdmin && (
          <div className="flex w-full justify-between">
            <div className="mr-4 pb-2">
              <SuperAdminFieldGroup caption={t('hub-settings.store-settings.show-confirmation-popup')}>
                {' '}
              </SuperAdminFieldGroup>
            </div>
            <Controller
              control={control}
              name="config.store_settings.show_confirmation_popup"
              render={({ field }) => (
                <Toggle
                  size="sm"
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  disabled={!canEdit}
                />
              )}
            />
          </div>
        )}

        {isSuperAdmin && (
          <div className="flex w-full justify-between">
            <div className="mr-4 pb-2">
              <SuperAdminFieldGroup caption={t('hub-settings.store-settings.show-subscription-agreement')}>
                {' '}
              </SuperAdminFieldGroup>
            </div>
            <Controller
              control={control}
              name="config.store_settings.show_subscription_agreement"
              render={({ field }) => (
                <Toggle
                  size="sm"
                  checked={field.value}
                  onChange={e => field.onChange(e.target.checked)}
                  disabled={!canEdit}
                />
              )}
            />
          </div>
        )}

        {isSuperAdmin && (
          <>
            <SuperAdminFieldGroup label={t('hub-settings.store-settings.mobile-columns')}>
              <Input
                min={0}
                type="number"
                {...register('config.store_settings.mobile_grid_columns', {
                  setValueAs: value => {
                    if (value == null) {
                      return null
                    }
                    return parseInt(value)
                  },
                })}
              />
            </SuperAdminFieldGroup>

            <SuperAdminFieldGroup label={t('hub-settings.store-settings.mobile-landscape-columns')}>
              <Input
                min={0}
                type="number"
                {...register('config.store_settings.mobile_landscape_grid_columns', {
                  setValueAs: value => {
                    if (value == null) {
                      return null
                    }
                    return parseInt(value)
                  },
                })}
              />
            </SuperAdminFieldGroup>

            <SuperAdminFieldGroup label={t('hub-settings.store-settings.tablet-columns')}>
              <Input
                min={0}
                type="number"
                {...register('config.store_settings.tablet_grid_columns', {
                  setValueAs: value => {
                    if (value == null) {
                      return null
                    }
                    return parseInt(value)
                  },
                })}
              />
            </SuperAdminFieldGroup>

            <SuperAdminFieldGroup label={t('hub-settings.store-settings.desktop-columns')}>
              <Input
                min={0}
                type="number"
                {...register('config.store_settings.desktop_grid_columns', {
                  setValueAs: value => {
                    if (value == null) {
                      return null
                    }
                    return parseInt(value)
                  },
                })}
              />
            </SuperAdminFieldGroup>

            <SuperAdminFieldGroup label={t('hub-settings.store-settings.large-desktop-columns')}>
              <Input
                min={0}
                type="number"
                {...register('config.store_settings.large_desktop_grid_columns', {
                  setValueAs: value => {
                    if (value == null) {
                      return null
                    }
                    return parseInt(value)
                  },
                })}
              />
            </SuperAdminFieldGroup>
          </>
        )}

        {isSuperAdmin && (
          <SuperAdminFieldGroup label={t('hub-settings.store-settings.store-version')}>
            <Input placeholder="v1" {...register('config.store_settings.store_version')} disabled={!canEdit} />
          </SuperAdminFieldGroup>
        )}

        {canEdit && (
          <FieldGroup label={t('hub-settings.store-settings.quick-store-button')}>
            <Controller
              control={control}
              name="config.store_settings.quick_store_button"
              render={({ field }) => (
                <Select
                  items={[
                    { value: QuickStoreButtonMode.Hidden, children: t('hub-settings.store-settings.hidden') },
                    {
                      value: QuickStoreButtonMode.ShowForAll,
                      children: t('hub-settings.store-settings.show-for-all-countries'),
                    },
                    { value: QuickStoreButtonMode.ShowForUs, children: t('hub-settings.store-settings.show-for-us') },
                  ]}
                  {...field}
                />
              )}
            />
          </FieldGroup>
        )}
      </Block>
    </DescBlock>
  )
}
